<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { type PropType, toRef } from 'vue'

import BaseCrossSectionDataTableRow from '@/components/calculation-result/BaseCrossSectionDataTableRow.vue'

import { formatValue } from '@/components/calculation-result/utils/formatValue'

import { useCrossSectionStore } from '@/stores/crossSection'
import type { CrossSectionType } from '@/stores/type/crossSection.type'

const crossSectionStore = useCrossSectionStore()

const { fields } = storeToRefs(crossSectionStore)

const props = defineProps({
  item: { type: Object as PropType<CrossSectionType>, required: true },
  tableHeader: { type: String, required: true },
  tableRowFields: { type: Array as PropType<(keyof CrossSectionType | null)[]>, default: () => [] },
  choiceFields: { type: Array as PropType<(keyof CrossSectionType)[]>, default: () => [] },
  unit: { type: String, default: '' }
})

const item = toRef(props, 'item')
const choiceFields = toRef(props, 'choiceFields')

const getUnit = (param: string): string => {
  if (props.unit) return props.unit
  const label = fields.value ? fields.value[param]?.label : ''
  let unit = '-'
  if (label.includes('[')) {
    unit = label.split('[')[1].split(']')[0]
  }
  return unit
}

const getChoices = (key: keyof CrossSectionType) => {
  return fields.value ? (fields.value[key]?.choices ?? []) : []
}

const getValue = (key: keyof CrossSectionType): string => {
  if (choiceFields.value.includes(key)) {
    const choices = getChoices(key)
    return choices.find((choice: any) => choice.value === item.value[key])?.display_name ?? ''
  }
  return formatValue(item.value[key])
}

const getRowValue = (
  param: keyof CrossSectionType | null
): { text: string; unit: string; value: any } | null => {
  if (!param) return null
  const label = fields.value ? fields.value[param]?.label : ''
  const text = label ? label.split('[')[0] : ''
  const unit = getUnit(param)
  const value = getValue(param)
  return { text, unit, value }
}
</script>

<template>
  <div class="d-flex flex-column">
    <v-table class="overflow-auto" density="compact" fixed-header hover>
      <thead>
        <tr>
          <th class="text-center border-top-grey pl-0" style="width: 58%">{{ tableHeader }}</th>
          <th class="text-center border-left-grey border-top-grey" style="width: 20%">Unit</th>
          <th
            class="text-center border-left-grey border-right-grey border-top-grey"
            style="width: 20%"
          >
            Value
          </th>
        </tr>
      </thead>
      <tbody v-if="item" class="text-center fill-height pb-3">
        <BaseCrossSectionDataTableRow
          v-for="(field, idx) in tableRowFields"
          :key="idx"
          :data="getRowValue(field)"
        />
        <tr>
          <td colspan="48" style="height: 0px"></td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td colspan="8" style="text-align: center">No data available</td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<style scoped>
th {
  text-align: center;
}
</style>
